// ReadingProgressBar — fixed 4px bar at the very top of the viewport.
// Fills from 0% → 100% as the user scrolls through the article body, with
// a small percentage badge in the top-right corner for a Medium-style read
// indicator. Auto-hides when the tracked element isn't in view.
import { useEffect, useState } from "react";

const ReadingProgressBar = ({ targetSelector = "article" }) => {
  const [pct, setPct] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const compute = () => {
      const el = document.querySelector(targetSelector);
      if (!el) { setVisible(false); setPct(0); return; }
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      const total = rect.height - viewportH;
      const scrolled = Math.min(Math.max(-rect.top, 0), Math.max(total, 1));
      const p = total > 0 ? Math.round((scrolled / total) * 100) : 0;
      setPct(Math.min(100, Math.max(0, p)));
      // Show once the top of the article has been reached (and until the bottom leaves the viewport).
      setVisible(rect.top < viewportH * 0.6 && rect.bottom > 0);
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [targetSelector]);

  return (
    <>
      <div
        className={`fixed top-0 left-0 right-0 h-1 z-50 bg-[color:var(--gv-line)]/40 transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}
        data-testid="reading-progress-bar"
      >
        <div
          className="h-full bg-[color:var(--gv-pink)] transition-[width] duration-150 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div
        className={`fixed top-3 right-4 z-50 px-2 py-0.5 rounded-full bg-white/85 backdrop-blur border border-[color:var(--gv-line)] text-[11px] font-medium text-[color:var(--gv-ink)] transition-opacity duration-300 tabular-nums ${visible && pct > 1 ? "opacity-100" : "opacity-0"}`}
        data-testid="reading-progress-pct"
      >
        {pct}%
      </div>
    </>
  );
};

export default ReadingProgressBar;
